import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../Modal';

interface AdminDashboardProps {
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [startTime, setStartTime] = useState<string | null>(null);
  // Nuovo state per memorizzare il timestamp grezzo di inizio registrazione
  const [rawStartTime, setRawStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  // Stato per le elaborazioni video (v3)
  const [pendingVideoProcessing, setPendingVideoProcessing] = useState<number>(0);
  // Stato per i video in coda (v3)
  const [videosInQueue, setVideosInQueue] = useState<number>(0);
  // Nuovi state per le statistiche aggiuntive
  const [buttonPressCount, setButtonPressCount] = useState<number>(0);
  const [assembledFiles, setAssembledFiles] = useState<number>(0);
  const [currentFolder, setcurrentFolder] = useState<string | null>(null);

  // State per salvare i counter dell'ultima sessione
  const [lastStats, setLastStats] = useState<{
    buttonPress: number;
    assembled: number;
    expected: number;
  } | null>(null);
  // Calcolo dei file attesi basato sui tocchi (es: tocchi * 2)
  const expectedFiles = buttonPressCount;

  // Nuovi state per heartbeat
  const [heartbeat, setHeartbeat] = useState<{ buttonOk: boolean }>({ buttonOk: true });
  const [errorDescription, setErrorDescription] = useState<string | null>(null);

  // Stato per mostrare modal dettagli heartbeat
  const [showDetails, setShowDetails] = useState(false);

  // Recupera lo stato dall'endpoint /status all'avvio del componente
  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch('https://api.asddonvittorio.it/status');
        const data = await response.json();
        if (data.isRecording) {
          setIsRecording(true);
          setcurrentFolder(data.currentFolder);
          if (data.recordingStartTime) {
            const startDate = new Date(data.recordingStartTime);
            setRawStartTime(startDate);
            const formattedStartTime = `${startDate.getHours().toString().padStart(2, '0')}:` +
              `${startDate.getMinutes().toString().padStart(2, '0')}:` +
              `${startDate.getSeconds().toString().padStart(2, '0')}`;
            setStartTime(formattedStartTime);
            const now = new Date();
            const elapsed = Math.floor((now.getTime() - startDate.getTime()) / 1000);
            setRecordingTime(Math.max(elapsed, 0));
          }
        } else {
          setIsRecording(false);
        }
        // Aggiorna le elaborazioni video in corso (v3)
        if (data.pendingVideoProcessing !== undefined) {
          setPendingVideoProcessing(data.pendingVideoProcessing);
        } else {
          setPendingVideoProcessing(0);
        }
        // Aggiorna i video in coda (v3)
        if (data.videosInQueue !== undefined) {
          setVideosInQueue(data.videosInQueue);
        } else {
          setVideosInQueue(0);
        }
        // Aggiorna anche il button press count se presente
        if (data.buttonPressCount !== undefined) {
          setButtonPressCount(data.buttonPressCount);
        }
        // aggiorna heartbeat
        if (data.heartbeat) {
          setHeartbeat({ buttonOk: data.heartbeat.buttonOk });
        }
        setErrorDescription(data.errorDescription || null);
      } catch (error) {
        console.error('Errore nel recuperare lo stato:', error);
      }
    };

    // Polling ogni 10 secondi per aggiornare lo stato (inclusi pendingUploads e pendingRetry)
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  // Polling per aggiornare il conteggio dei file assemblati
  React.useEffect(() => {
    const fetchAssembledFiles = async () => {
      try {
        const response = await fetch('https://api.asddonvittorio.it/assembled-files');
        const data = await response.json();
        setAssembledFiles(data.assembledCount);
      } catch (error) {
        console.error('Errore nel recuperare il conteggio dei file assemblati:', error);
      }
    };
    fetchAssembledFiles();
    const interval = setInterval(fetchAssembledFiles, 10000);
    return () => clearInterval(interval);
  }, []);

  // Timer: ogni secondo ricalcola il tempo trascorso dalla partenza 
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording && rawStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - rawStartTime.getTime()) / 1000);
        setRecordingTime(Math.max(elapsed, 0));
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording, rawStartTime]);

  // Formatta il tempo in formato mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // Ottieni l'ora corrente formattata (HH:mm:ss)
  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:` +
      `${now.getMinutes().toString().padStart(2, '0')}:` +
      `${now.getSeconds().toString().padStart(2, '0')}`;
  };

  // Protezione avvio registrazione se manca heartbeat
  const startRecording = async () => {
    if (!heartbeat.buttonOk) {
      alert(`Impossibile avviare registrazione: ${errorDescription}`);
      return;
    }
    try {
      console.log('Avvio registrazione...');
      await fetch('https://api.asddonvittorio.it/start-recording', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const now = new Date();
      setRawStartTime(now);
      setStartTime(getCurrentTime());
      setEndTime(null);
      setIsRecording(true);
      setPendingVideoProcessing(0);
      setVideosInQueue(0);
      // Resetta i valori dell'ultima sessione
      setLastStats(null);
    } catch (error) {
      console.error('Errore durante l\'avvio della registrazione:', error);
      alert('Errore nell\'avvio della registrazione. Riprova più tardi.');
    }
  };

  const stopRecording = async () => {
    if (pendingVideoProcessing > 0 || videosInQueue > 0) {
      alert('Non è possibile terminare la registrazione: elaborazioni video o video in coda in corso. Attendere il completamento oppure usa "Forza Arresto".');
      return;
    }
    try {
      console.log("Arresto registrazione...");
      await fetch("https://api.asddonvittorio.it/stop-recording", {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      // Cattura i valori necessari in variabili locali
      const newEndTime = getCurrentTime();
      const currentStats = {
        buttonPress: buttonPressCount,
        assembled: assembledFiles,
        expected: expectedFiles
      };
      // Aggiorna lo state
      setEndTime(newEndTime);
      setLastStats(currentStats);
      setIsRecording(false);
      setButtonPressCount(0);
      setAssembledFiles(0);
      // Salva il log usando i valori locali (così non fai affidamento su state non ancora aggiornati)

      const report = `Registrazione terminata\nInizio: ${startTime}\nFine: ${newEndTime}\nTocchi totali: ${currentStats.buttonPress}\nFile assemblati: ${currentStats.assembled}\nFile attesi: ${currentStats.expected}`;
      await fetch("https://api.asddonvittorio.it/save-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentFolder, report })
      });
    } catch (error) {
      console.error("Errore durante l'arresto della registrazione:", error);
      alert("Errore nell'arresto della registrazione. Riprova più tardi.");
    }
  };

  const forceStopRecording = async () => {
    try {
      const confirm = window.confirm("Sei sicuro di voler forzare l'arresto della registrazione? Potrebbe causare errori di sistema.");
      if (!confirm) return;

      console.log("Forza arresto registrazione...");
      await fetch("https://api.asddonvittorio.it/stop-recording-force", {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      const newEndTime = getCurrentTime();
      const currentStats = {
        buttonPress: buttonPressCount,
        assembled: assembledFiles,
        expected: buttonPressCount * 2
      };
      setEndTime(newEndTime);
      setLastStats(currentStats);
      setIsRecording(false);
      setButtonPressCount(0);
      setAssembledFiles(0);
      const registrationCode = `${startTime!.replace(/:/g, "")}-${newEndTime.replace(/:/g, "")}`;
      const report = `Registrazione terminata\nInizio: ${startTime}\nFine: ${newEndTime}\nTocchi totali: ${currentStats.buttonPress}\nFile assemblati: ${currentStats.assembled}\nFile attesi: ${currentStats.expected}`;
      await fetch("https://api.asddonvittorio.it/save-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ registrationCode, report })
      });
    } catch (error) {
      console.error("Errore durante il forzamento dell'arresto della registrazione:", error);
      alert("Errore nel forzare l'arresto della registrazione. Riprova più tardi.");
    }
  };

  const debugQueue = async () => {
    try {
      console.log("Debug coda video...");
      const response = await fetch("https://api.asddonvittorio.it/queue-debug");
      const data = await response.json();

      const debugInfo = `
Debug Coda Video:
- Video in coda: ${data.queueStatus.inQueue}
- Elaborazione in corso: ${data.queueStatus.processing ? 'Sì' : 'No'}
- Counter globale pendingVideoProcessing: ${data.globalState.pendingVideoProcessing}
- Counter globale videosInQueue: ${data.globalState.videosInQueue}
- Timestamp: ${data.timestamp}
      `;

      alert(debugInfo);
      console.log("Debug coda video:", data);
    } catch (error) {
      console.error("Errore nel debug della coda:", error);
      alert("Errore nel debug della coda. Controlla la console per i dettagli.");
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col justify-center items-center pt-20 p-4 md:p-6">

      {/* Header alert banner */}
      <div className="absolute top-0 w-full px-4 bg-gradient-to-r from-yellow-300 to-yellow-200 text-center py-2 md:py-3 text-gray-700 font-semibold shadow-md animate-pulse">
        <div className="flex items-center justify-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-5 w-4 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm md:text-base">Perro fai attenzione a quello che fai, questa è un'area riservata! 🤨</span>
        </div>
      </div>

      <div className="max-w-5xl w-full mt-8">
        {/* Header with shadow effect */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 md:mb-6 bg-white p-3 md:p-4 rounded-lg shadow-lg backdrop-blur-sm bg-opacity-90">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="bg-blue-600 h-8 w-8 md:h-10 md:w-10 rounded-full flex items-center justify-center shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-6 w-4 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Dashboard Amministrazione
            </h1>
          </div>
          <button
            onClick={onLogout}
            className="mt-2 md:mt-0 px-3 py-1 md:px-4 md:py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 shadow-md transition-all duration-300 flex items-center gap-1 md:gap-2 group text-sm md:text-base"
          >
            <span>Logout</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-5 w-4 md:w-5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>

        {/* Main content card with glass effect */}
        <div className="bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-6 md:p-10 rounded-2xl shadow-xl border border-gray-100">
          {/* Status indicator section */}
          <div className="mb-8 md:mb-12">
            <div className={`text-xl md:text-3xl font-bold mb-4 md:mb-6 text-center ${isRecording ? 'text-red-600' : 'text-gray-700'} transition-colors duration-300`}>
              {isRecording ? (
                <div className="flex items-center justify-center gap-2 md:gap-3">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-red-600 rounded-full opacity-20 animate-ping"></div>
                    <div className="h-3 w-3 md:h-4 md:w-4 rounded-full bg-red-600"></div>
                  </div>
                  <span>REGISTRAZIONE IN CORSO</span>
                </div>
              ) : (
                // Indica sistema non pronto e pulsante maggiori dettagli
                (!heartbeat.buttonOk) ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-red-600 font-bold">SISTEMA NON PRONTO PER REGISTRARE</div>
                    <button
                      className="text-blue-600 underline"
                      onClick={() => setShowDetails(true)}
                    >Maggiori dettagli</button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 md:gap-3">
                    <span className="text-green-600">●</span>
                    <span>SISTEMA PRONTO PER REGISTRARE</span>
                  </div>
                )
              )}
            </div>

            {isRecording && (
              <div className="flex flex-col justify-center items-center gap-3 animate-fadeIn">
                <div className="h-28 w-28 md:h-32 md:w-32 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-xl">
                  <div className="h-24 w-24 md:h-28 md:w-28 rounded-full bg-white flex items-center justify-center">
                    <div className="text-2xl md:text-3xl font-mono font-bold text-red-600">
                      {formatTime(recordingTime)}
                    </div>
                  </div>
                </div>
                {startTime && (
                  <div className="text-gray-600 mt-2 bg-red-50 px-3 py-1 md:px-4 md:py-2 rounded-full shadow-inner text-sm md:text-base">
                    Inizio registrazione: <span className="font-semibold text-red-700">{startTime.slice(0, 5)}</span>
                  </div>
                )}
              </div>
            )}

            {!isRecording && startTime && endTime && (
              <div className="text-gray-600 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 md:p-5 rounded-xl border border-blue-100 shadow-inner transform transition-all hover:scale-105">
                <div className="text-center mb-3">
                  <span className="font-bold text-base md:text-lg text-blue-800">Ultima registrazione</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm">
                    <div className="text-xs text-blue-500 uppercase font-semibold">Inizio</div>
                    <div className="text-lg md:text-xl font-bold text-blue-800">{startTime.slice(0, 5)}</div>
                  </div>
                  <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm">
                    <div className="text-xs text-blue-500 uppercase font-semibold">Fine</div>
                    <div className="text-lg md:text-xl font-bold text-blue-800">{endTime.slice(0, 5)}</div>
                  </div>
                </div>

                {lastStats && (
                  <div className="mt-4 grid grid-cols-3 gap-2 md:gap-3">
                    <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm text-center">
                      <div className="text-xs text-blue-500 uppercase font-semibold">Tocchi</div>
                      <div className="text-lg md:text-xl font-bold text-blue-800">{lastStats.buttonPress}</div>
                    </div>
                    <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm text-center">
                      <div className="text-xs text-blue-500 uppercase font-semibold">Assemblati</div>
                      <div className="text-lg md:text-xl font-bold text-blue-800">{lastStats.assembled}</div>
                    </div>
                    <div className="bg-white p-2 md:p-3 rounded-lg shadow-sm text-center">
                      <div className="text-xs text-blue-500 uppercase font-semibold">Attesi</div>
                      <div className="text-lg md:text-xl font-bold text-blue-800">{lastStats.expected}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stats boxes with modern design */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Left column - Video Processing stats */}
              <div className="col-span-1">
                <div className={`h-full rounded-xl shadow-md transition-all duration-300 overflow-hidden ${pendingVideoProcessing > 0 ? 'bg-gradient-to-br from-blue-200 to-blue-100' : 'bg-gradient-to-br from-green-200 to-green-100'}`}>
                  <div className="p-3 md:p-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      {pendingVideoProcessing > 0 ? (
                        <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-500 flex items-center justify-center shadow-md">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600">Elaborazioni Video</div>
                        <div className={`font-bold ${pendingVideoProcessing > 0 ? 'text-blue-800' : 'text-green-800'}`}>
                          {pendingVideoProcessing > 0 ? `${pendingVideoProcessing} video in elaborazione` : 'Nessuna'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`h-1 w-full ${pendingVideoProcessing > 0 ? 'bg-blue-400' : 'bg-green-500'}`}></div>
                </div>
              </div>

              {/* Middle column - Videos in queue */}
              <div className="col-span-1">
                <div className={`h-full rounded-xl shadow-md transition-all duration-300 overflow-hidden ${videosInQueue > 0 ? 'bg-gradient-to-br from-purple-200 to-purple-100' : 'bg-gradient-to-br from-green-200 to-green-100'}`}>
                  <div className="p-3 md:p-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      {videosInQueue > 0 ? (
                        <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-purple-500 flex items-center justify-center shadow-md">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                      ) : (
                        <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600">Video in Coda</div>
                        <div className={`font-bold ${videosInQueue > 0 ? 'text-purple-800' : 'text-green-800'}`}>
                          {videosInQueue > 0 ? `${videosInQueue} video in attesa` : 'Nessuno in coda'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`h-1 w-full ${videosInQueue > 0 ? 'bg-purple-400' : 'bg-green-500'}`}></div>
                </div>
              </div>

              {/* Right column - Current folder */}
              <div className="col-span-1">
                <div className="h-full bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl shadow-md overflow-hidden">
                  <div className="p-3 md:p-4">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-500 flex items-center justify-center shadow-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600">Cartella</div>
                        <div className="font-bold text-blue-800 truncate">
                          {currentFolder ? currentFolder : 'Nessuna cartella attiva'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-blue-400"></div>
                </div>
              </div>
            </div>

            {/* Statistics counter cards */}
            <div className="mt-4 md:mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Totale Tocchi */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg p-4 md:p-6 transform transition-all hover:scale-105 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Tocchi totali</div>
                    <div className="text-2xl md:text-3xl font-bold mt-2">{buttonPressCount}</div>
                  </div>
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <rect x="4" y="9" width="16" height="6" rx="3" ry="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
                <div className="mt-2 h-1 w-full bg-white bg-opacity-30 rounded-full overflow-hidden">
                  <div className="h-full bg-white" style={{ width: `${Math.min(buttonPressCount / 100 * 100, 100)}%` }}></div>
                </div>
              </div>

              {/* File Assemblati */}
              <div className="bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-xl shadow-lg p-4 md:p-6 transform transition-all hover:scale-105 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider opacity-80">File assemblati</div>
                    <div className="text-2xl md:text-3xl font-bold mt-2">{assembledFiles}</div>
                  </div>
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2" />
                    </svg>
                  </div>
                </div>
                <div className="mt-2 h-1 w-full bg-white bg-opacity-30 rounded-full overflow-hidden">
                  <div className="h-full bg-white" style={{ width: `${expectedFiles > 0 ? (assembledFiles / expectedFiles) * 100 : 0}%` }}></div>
                </div>
              </div>

              {/* File Attesi */}
              <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl shadow-lg p-4 md:p-6 transform transition-all hover:scale-105 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider opacity-80">File attesi</div>
                    <div className="text-2xl md:text-3xl font-bold mt-2">{expectedFiles}</div>
                  </div>
                  <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="mt-2 flex items-center">
                  <div className="text-xs opacity-80">
                    {assembledFiles > 0 && expectedFiles > 0 ?
                      `${Math.round((assembledFiles / expectedFiles) * 100)}% completato` :
                      'Nessun progresso'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons section */}
          <div className="flex flex-col items-center justify-center gap-4 md:gap-6">
            {!isRecording ? (
              // pulsante Avvia con stile disabled diverso
              (() => {
                const isStartDisabled = !heartbeat.buttonOk || isRecording;
                return (
                  <button
                    onClick={startRecording}
                    disabled={isStartDisabled}
                    className={`w-full max-w-md py-4 md:py-6 text-white text-lg md:text-xl font-bold rounded-xl shadow-xl flex items-center justify-center gap-3 transform transition-all duration-300 ${isStartDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-105 hover:shadow-2xl'}`}
                  >
                    <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 md:h-8 w-6 md:w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" fill="currentColor" />
                      </svg>
                    </div>
                    <span className={`transition-transform ${isStartDisabled ? '' : 'group-hover:scale-110'}`}>AVVIA REGISTRAZIONE</span>
                  </button>
                );
              })()
            ) : (
              <>
                <button
                  onClick={stopRecording}
                  disabled={pendingVideoProcessing > 0 || videosInQueue > 0}
                  className={`w-full max-w-md py-4 md:py-6 ${pendingVideoProcessing > 0 || videosInQueue > 0
                    ? 'bg-gradient-to-r from-gray-400 to-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 animate-pulse'
                    } text-white text-lg md:text-xl font-bold rounded-xl shadow-xl flex items-center justify-center gap-3 transform transition-all duration-300`}
                >
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 md:h-8 w-6 md:w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <rect x="6" y="6" width="12" height="12" fill="currentColor" />
                    </svg>
                  </div>
                  <span>FERMA REGISTRAZIONE</span>
                </button>

                {(pendingVideoProcessing > 0 || videosInQueue > 0) && (
                  <button
                    onClick={forceStopRecording}
                    className="w-full max-w-md py-3 md:py-4 bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 text-white text-base md:text-lg font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 md:gap-3 transform transition-all duration-300 hover:scale-105"
                  >
                    <div className="flex items-center justify-center h-7 w-7 md:h-8 md:w-8 rounded-full bg-white bg-opacity-20">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 md:h-6 w-5 md:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="relative">
                      <span className="absolute -top-1 -right-1 h-2 w-2 md:h-3 md:w-3 bg-yellow-300 rounded-full animate-ping"></span>
                      FORZA ARRESTO
                    </span>
                  </button>
                )}
              </>
            )}

            <Link
              to="/admin/uploads"
              className="w-full max-w-md py-2 md:py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-sm md:text-base font-semibold rounded-xl shadow-lg flex items-center justify-center gap-2 transform transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-center justify-center h-6 w-6 md:h-7 md:w-7 rounded-full bg-white bg-opacity-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-5 w-4 md:w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <span>GESTIONE UPLOADS</span>
            </Link>

            {/* Debug button - 
            <button
              onClick={debugQueue}
              className="w-full max-w-md py-2 md:py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm md:text-base font-semibold rounded-xl shadow-lg flex items-center justify-center gap-2 transform transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-center justify-center h-6 w-6 md:h-7 md:w-7 rounded-full bg-white bg-opacity-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-5 w-4 md:w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              DEBUG CODA
            </button>*/}
          </div>
        </div>
      </div>

      {/* Footer with animated hover effect */}
      <footer className="mt-6 md:mt-12 w-full max-w-md mx-auto text-xs md:text-sm text-gray-600 bg-white bg-opacity-50 backdrop-filter backdrop-blur-sm py-2 md:py-3 px-4 md:px-6 rounded-full shadow-md transform transition-all hover:scale-105">
        <a
          href="https://www.instagram.com/vittorio.carcatella"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-wrap justify-center items-center gap-1 md:gap-2 hover:text-blue-600 transition-colors"
        >
          <span className="italic">
            Fatto con il
            <span className="inline-block mx-1 text-red-500 animate-pulse transform hover:scale-125 transition-all">❤️</span>
            da
          </span>
          <span className="font-bold text-blue-500 border-b border-blue-200 pb-px">Vittorio Carcatella</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 md:h-4 w-3 md:w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </footer>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>

      {/* Modal per dettagli heartbeat */}
      {showDetails && (
        <Modal onClose={() => setShowDetails(false)}>
          <h2 className="text-xl font-bold mb-4">Dettagli Errore</h2>
          <p className="text-gray-700">{errorDescription}</p>
        </Modal>
      )}
    </div>
  );
};



export default AdminDashboard;